import { emptyFragment } from '@execonline-inc/execonline-ui';
import { isJust, Maybe as StructuredMaybe } from '@execonline-inc/structured-maybe';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T } from '../../../../../Translations';
import InlineSpinner from '../../../../Cutesies/InlineSpinner';
import { familyCourseOptions, optionForFamily } from '../CourseField/CourseOptions';
import { shareStore } from '../Stores/ShareStore';
import { Program, ShareBy, ShareType } from '../Stores/ShareStore/Types';
import DoneButton from './DoneButton';
import NextButton from './NextButton';
import ParticipantAccessToggle from './ParticipantAccessToggle';
import ShareMethodSelector from './ShareMethodSelector';
import ShareTypeSelector from './ShareTypeSelector';
import ShareUrl from './ShareUrl';

interface Props {
  collectionId: number;
  shareBy: ShareBy;
  setShareBy: (shareBy: ShareBy) => void;
  shareType: ShareType;
  setShareType: (shareType: ShareType) => void;
  selectedProgramFamilyId: Maybe<number>;
  setSelectedProgramFamilyId: (programFamilyId: Maybe<number>) => void;
  close: () => void;
  publishedAt: StructuredMaybe<Date>;
  onTogglePublished: () => void;
  isPublishing: boolean;
  returnPath?: string;
}

const Content: React.FC<Props> = ({
  collectionId,
  shareBy,
  setShareBy,
  shareType,
  setShareType,
  selectedProgramFamilyId,
  setSelectedProgramFamilyId,
  close,
  publishedAt,
  onTogglePublished,
  isPublishing,
  returnPath,
}) => {
  // Check if collection is published to determine toggle state
  const isPublished = isJust(publishedAt);

  // Handler for program family selection (URL flow) - fetches ShareUrl
  const handleProgramFamilyChange = (programFamilyId: Maybe<number>) => {
    setSelectedProgramFamilyId(programFamilyId);

    // Fetch program family ShareUrl when selection changes
    programFamilyId.cata({
      Just: (id) => {
        shareStore.fetchProgramShareUrl(id);
      },
      Nothing: () => {
        shareStore.clearProgramShareUrl();
      },
    });
  };

  // Switching to program access preselects nothing: the course is chosen on
  // the picker below (URL) or on the invite page (email), DVLP-8060.
  const handleShareTypeChange = (newShareType: ShareType) => {
    setShareType(newShareType);

    if (newShareType === 'collection') {
      shareStore.clearProgramShareUrl();
    }
  };

  // The URL to copy exists once there is something to point it at: the
  // collection, or the course the admin has chosen. The course counts only
  // if the collection still offers it, the same test the card makes.
  const hasShareTarget = (programs: ReadonlyArray<Program>): boolean => {
    switch (shareType) {
      case 'collection':
        return true;
      case 'program':
        return selectedProgramFamilyId
          .andThen((id) => optionForFamily(familyCourseOptions(programs), id))
          .isJust();
      default:
        return assertNever(shareType);
    }
  };

  switch (shareStore.state.kind) {
    case 'waiting':
    case 'loading':
      return (
        <div className="flex items-center justify-center py-8">
          <InlineSpinner size={48} />
        </div>
      );
    case 'publishing':
      return (
        <>
          <ParticipantAccessToggle
            isPublished={isPublished}
            onTogglePublished={onTogglePublished}
            isPublishing={isPublishing}
          />

          <hr className="border-border mb-8" />

          <div className="flex items-center justify-center py-8">
            <InlineSpinner size={48} />
          </div>
        </>
      );
    case 'refreshing':
    case 'reviewing':
    case 'searching':
    case 'confirming':
    case 'submitting':
      return emptyFragment();
    case 'ready':
      // min-h on the ready-state wrapper keeps the modal a constant size
      // when toggling between Share by Email and Share by URL (the URL flow
      // adds a URL input + Copy button which are taller than the Email
      // flow's Next button). Sized to fit the tallest resting state, Share
      // by URL with a course chosen (card with one row of start-date chips,
      // URL, Copy URL), so the others pad up to match. Advanced Settings
      // lives in a popover and the course catalog floats, so this min-h is
      // only exceeded when the admin expands the "+N more" chips, which is
      // their own choice. DVLP-7559, DVLP-8060.
      return (
        <div className="min-h-[600px] flex flex-col">
          <ParticipantAccessToggle
            isPublished={isPublished}
            onTogglePublished={onTogglePublished}
            isPublishing={isPublishing}
          />

          <hr className="border-border mb-8" />

          <ShareMethodSelector shareBy={shareBy} onChange={setShareBy} />
          <ShareTypeSelector
            shareType={shareType}
            onChange={handleShareTypeChange}
            programs={shareStore.state.learningCollectionResource.payload.programs}
            programShareDisabled={
              shareStore.state.learningCollectionResource.payload.programs.length === 0
            }
            shareBy={shareBy}
            selectedProgramFamilyId={selectedProgramFamilyId}
            onProgramFamilyChange={handleProgramFamilyChange}
          />

          {shareBy === 'url' &&
          hasShareTarget(shareStore.state.learningCollectionResource.payload.programs) ? (
            <ShareUrl />
          ) : null}

          {/*
           * mt-auto pins the button row to the bottom of the flex column so
           * the Done (URL flow) and Next (Email flow) buttons land at the
           * same vertical position regardless of how much content is above
           * them. DVLP-7559.
           */}
          <div className="flex justify-end mt-auto pt-6">
            {shareBy === 'url' ? (
              <DoneButton isPublished={isPublished} isPublishing={isPublishing} close={close} />
            ) : (
              <NextButton
                collectionId={collectionId}
                isPublished={isPublished}
                isPublishing={isPublishing}
                shareBy={shareBy}
                shareType={shareType}
                returnPath={returnPath}
              />
            )}
          </div>
        </div>
      );
    case 'error':
      return (
        <div className="bg-red-50 border border-red-600 rounded p-4 mb-4">
          <p className="text-sm text-red-600">
            <T kind={shareStore.state.message} />
          </p>
        </div>
      );
    default:
      assertNever(shareStore.state);
  }
};

export default observer(Content);
